import { z } from 'zod';
import {
  acquisitionTypeOptions,
  applicantIdentityOptions,
  constructionTypeOptions,
  organizationTypeOptions,
  professionalTrainingOptions,
  projectByRightOptions,
  siteControlStatusOptions,
  yesNoOptions,
} from './gmfDeveloperPipeline.constants';
import type { GmfProjectFile } from './types/gmfDeveloperPipeline.types';

const requiredText = (label: string) => z.string().trim().min(1, `${label} is required`);
const optionalText = z.string().optional().default('');
const zipCode = z
  .string()
  .trim()
  .regex(/^\d{5}$/, 'ZIP code must be 5 digits');
const currencyLike = (label: string) =>
  z
    .string()
    .trim()
    .min(1, `${label} is required`)
    .refine((value) => Number(value.replace(/[$,\s]/g, '')) > 0, `${label} must be greater than 0`);

export const gmfProjectDetailsSchema = z.object({
  projectDetails: z.object({
    streetAddress: requiredText('Street address'),
    city: z.literal('Milwaukee'),
    state: z.literal('Wisconsin'),
    dealType: z.literal('Homeownership'),
    zipCode,
    proposedUnits: z
      .string()
      .trim()
      .min(1, 'Proposed units is required')
      .refine((value) => Number(value) > 0 && Number.isInteger(Number(value)), 'Proposed units must be a positive whole number'),
    projectByRight: z.enum(projectByRightOptions as [string, ...string[]], { error: 'Project by-right status is required' }),
    constructionType: z.enum(constructionTypeOptions as [string, ...string[]], { error: 'Construction type is required' }),
    acquisitionType: z.enum(acquisitionTypeOptions as [string, ...string[]], { error: 'Acquisition type is required' }),
    siteControlStatus: z.enum(siteControlStatusOptions as [string, ...string[]], { error: 'Site control status is required' }),
    acquisitionCostEstimate: currencyLike('Estimated acquisition cost'),
    developmentCostEstimate: currencyLike('Estimated development cost'),
    preDevelopmentCostEstimate: currencyLike('Estimated predevelopment cost'),
    communityNeed: requiredText('Community need'),
    targetPopulation: requiredText('Target population'),
    preliminaryFinancingStrategy: requiredText('Preliminary financing strategy'),
    preliminaryDevelopmentTimeline: requiredText('Preliminary development timeline'),
    keyPartners: optionalText,
    selectedFiles: z.array(z.instanceof(File)).optional(),
    additionalDocs: z.array(z.custom<GmfProjectFile>()).default([]),
  }),
});

export const gmfApplicantInformationSchema = z.object({
  applicantInformation: z
    .object({
      entityName: requiredText('Entity name'),
      businessEntityState: requiredText('Business entity state of organization'),
      website: z
        .string()
        .trim()
        .optional()
        .default('')
        .refine((value) => value.length === 0 || /^https?:\/\/.+|^[\w.-]+\.[a-z]{2,}/i.test(value), 'Website format is invalid'),
      formationDate: requiredText('Formation date'),
      businessStreetAddress: requiredText('Business street address'),
      businessCity: requiredText('Business city'),
      businessState: requiredText('Business state'),
      businessZipCode: zipCode,
      contactPersonName: requiredText('Contact person full name'),
      contactPersonEmail: z.string().trim().email('Contact person email is invalid'),
      phone: z.string().trim().min(10, 'Phone number is required'),
      dateOfBirth: requiredText('Date of birth'),
      residentialAddressDifferent: z.enum(yesNoOptions),
      residentialStreetAddress: optionalText,
      residentialCity: optionalText,
      residentialState: optionalText,
      residentialZipCode: optionalText,
      acreGraduate: z.enum(yesNoOptions, { error: 'ACRE graduate selection is required' }),
      applicantIdentity: z.array(z.enum(applicantIdentityOptions as [string, ...string[]])).default([]),
      educationBackground: requiredText('Educational background'),
      professionalLicenses: optionalText,
      professionalTrainingHistory: z.array(z.enum(professionalTrainingOptions as [string, ...string[]])).default([]),
      realEstateEntities: optionalText,
      currentEmployerRole: optionalText,
      organizationType: z.enum(organizationTypeOptions as [string, ...string[]], { error: 'Organization type is required' }),
    })
    .superRefine((value, ctx) => {
      if (value.residentialAddressDifferent !== 'Yes') return;

      if (!value.residentialStreetAddress.trim()) {
        ctx.addIssue({ code: 'custom', path: ['residentialStreetAddress'], message: 'Residential street address is required' });
      }
      if (!value.residentialCity.trim()) {
        ctx.addIssue({ code: 'custom', path: ['residentialCity'], message: 'Residential city is required' });
      }
      if (!value.residentialState.trim()) {
        ctx.addIssue({ code: 'custom', path: ['residentialState'], message: 'Residential state is required' });
      }
      if (!/^\d{5}$/.test(value.residentialZipCode)) {
        ctx.addIssue({ code: 'custom', path: ['residentialZipCode'], message: 'Residential ZIP code must be 5 digits' });
      }
    }),
});

const completedProjectSchema = z.object({
  projectName: requiredText('Project name'),
  address: requiredText('Project address'),
  units: z
    .string()
    .trim()
    .min(1, 'Units is required')
    .refine((value) => Number(value) > 0 && Number.isInteger(Number(value)), 'Units must be a positive whole number'),
  role: requiredText('Role'),
  completionYear: z
    .string()
    .trim()
    .min(4, 'Completion year is required')
    .refine((value) => /^\d{4}$/.test(value), 'Completion year must be four digits'),
});

export const gmfExperienceSchema = z.object({
  experience: z.object({
    companyCompleted: z.array(completedProjectSchema).max(3, 'Add up to three company projects'),
    principalCompleted: z.array(completedProjectSchema).max(3, 'Add up to three principal projects'),
  }),
});

export const gmfReviewSchema = z.object({});

export const gmfStepSchemas = [gmfProjectDetailsSchema, gmfApplicantInformationSchema, gmfExperienceSchema, gmfReviewSchema];

export const gmfDeveloperPipelineSchema = gmfProjectDetailsSchema.merge(gmfApplicantInformationSchema).merge(gmfExperienceSchema);

export type GmfDeveloperPipelineSchemaValues = z.infer<typeof gmfDeveloperPipelineSchema>;
